import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { VoiceProvider, useVoice } from '@humeai/voice-react';
import ProblemPanel from '../components/ProblemPanel';
import CodeEditor from '../components/CodeEditor';
import AudioControls from '../components/AudioControls';
import PreferencesModal from '../components/PreferencesModal';
import EarlySubmissionModal from '../components/EarlySubmissionModal';
import UsageWarningModal from '../components/UsageWarningModal';
import ReportModal from '../components/ReportModal';
import { fetchHumeAccessToken } from '../services/humeService';
import {
  generateQuestion,
  generateFreeFormQuestion,
} from '../services/questionService';
import { getSystemPrompt } from '../utils/systemPrompt';
import { Problem } from '../types/Problem';
import {
  createLeetCodeProblem,
  LeetCodePreferences as LeetCodePrefs,
} from '../utils/leetCodeUtils';
import { useAuth } from '../contexts/AuthContext';
import { useMembership } from '../contexts/MembershipContext';
import { useSessionTimer } from '../hooks/useSessionTimer';
import membershipService from '../services/membershipService';
import analysisService from '../services/analysisService';
import reportService from '../services/reportService';
// import { excellentInterviewRequest } from '../mocks/analysisRequestMocks';
import { INVALID_TIME_IN_MIN } from '../consts/InterviewTimeValidation';

interface StructuredPreferences {
  difficulty: string;
  area: string;
  companyStyle: string;
}

interface FreeFormPreferences {
  freeFormText: string;
}

type Preferences = StructuredPreferences | FreeFormPreferences | LeetCodePrefs;

const InterviewContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Extract problem from React Router state
  const providedProblem =
    (location.state?.problem as Problem | undefined) || null;

  const { connect, disconnect, status, sendSessionSettings, chatMetadata } =
    useVoice();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [language, setLanguage] = useState('python');
  const [showPreferences, setShowPreferences] = useState(false); // Will be set based on membership status
  const [preferences, setPreferences] = useState<Preferences | null>(null);
  const [isStructuredPreferences, setIsStructuredPreferences] =
    useState<boolean>(true);
  const [isLeetCodePreferences, setIsLeetCodePreferences] =
    useState<boolean>(false);
  const [currentProblem, setCurrentProblem] = useState<Problem | null>(null);
  const [isLoadingQuestion, setIsLoadingQuestion] = useState(false);
  const [questionError, setQuestionError] = useState<string | null>(null);
  const [loadingStep, setLoadingStep] = useState<
    'question' | 'token' | 'connecting' | 'ready'
  >('question');
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [totalTime, setTotalTime] = useState<number>(0);
  const [timerPaused, setTimerPaused] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const timerUpdateRef = useRef<NodeJS.Timeout | null>(null);
  const timeRemainingRef = useRef(timeRemaining);
  const { user } = useAuth();
  const { membershipInfo, loading: membershipLoading } = useMembership();

  // Session tracking state
  const [sessionTimer, sessionActions] = useSessionTimer();
  const [isSubmittingUsage, setIsSubmittingUsage] = useState(false);

  // Code tracking state
  const [codeHistory, setCodeHistory] = useState<
    Array<{
      timestamp: number;
      code: string;
    }>
  >([]);
  const sessionStartTimeRef = useRef<number>(0);
  const chatGroupIdRef = useRef<string>('');

  const [showEarlySubmissionModal, setShowEarlySubmissionModal] =
    useState(false);

  // Report modal state
  const [showReportModal, setShowReportModal] = useState(false);

  // Request cancellation
  const abortControllerRef = useRef<AbortController | null>(null);

  // Usage warning states
  const [showUsageWarning, setShowUsageWarning] = useState(false);
  const [usageWarningType, setUsageWarningType] = useState<
    'warning' | 'exceeded'
  >('warning');
  const [hasShownFiveMinuteWarning, setHasShownFiveMinuteWarning] =
    useState(false);
  const [hasShownExeedingWarning, setHasShownExeedingWarning] = useState(false);

  // Cleanup effect to abort requests on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Redirect logged-out users to landing page
  useEffect(() => {
    if (!user) {
      navigate('/');
    }
  }, [user, navigate]);

  // Set problem from URL parameters if provided
  useEffect(() => {
    if (providedProblem) {
      handlePresetQuestion();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providedProblem]);

  // Check membership status and determine whether to show preferences
  useEffect(() => {
    if (!membershipLoading && membershipInfo && !providedProblem) {
      const subscriptionStatus =
        membershipInfo.billing_period.subscription_status;

      if (subscriptionStatus === 'inactive' || membershipInfo.tier === 1) {
        // const defaultPreferences: StructuredPreferences = {
        //   difficulty: 'Easy',
        //   area: '',
        //   companyStyle: '',
        // };
        // // Generate question with default preferences
        // handlePreferencesConfirm(defaultPreferences, true);
        //
        // For inactive users or free tier, show preferences with easy difficulty only
        setShowPreferences(true);
      } else {
        // For active paid users, show preferences modal
        setShowPreferences(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membershipLoading, membershipInfo, providedProblem]);

  // Monitor usage and show warnings
  useEffect(() => {
    if (!membershipInfo || !sessionTimer.elapsedSeconds || showUsageWarning)
      return;

    const minutesUsed = sessionActions.getMinutesUsed();
    const remainingMinutes = membershipInfo.remaining_minutes - minutesUsed;

    // Show 5-minute warning (only once per session)
    if (
      !hasShownFiveMinuteWarning &&
      remainingMinutes <= 5 &&
      remainingMinutes > 0
    ) {
      setHasShownFiveMinuteWarning(true);
      setUsageWarningType('warning');
      setShowUsageWarning(true);
      handlePause();
      return;
    }

    // Show usage exceeded modal
    if (!hasShownExeedingWarning && remainingMinutes <= 0) {
      setHasShownExeedingWarning(true);
      setUsageWarningType('exceeded');
      setShowUsageWarning(true);
      handlePause();
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    membershipInfo,
    sessionTimer.elapsedSeconds,
    showUsageWarning,
    hasShownFiveMinuteWarning,
    hasShownExeedingWarning,
  ]);

  // Update ref when timeRemaining changes
  useEffect(() => {
    timeRemainingRef.current = timeRemaining;
  }, [timeRemaining]);

  // Initialize code and connection status
  const getInitialCode = (lang: string) => {
    const templates = {
      javascript: `function yourSolution(nums, target) {
    // Your solution here
    
}`,
      python: `def yourSolution(nums, target):
    # Your solution here
    pass`,
      java: `class Solution {
    public int[] yourSolution(int[] nums, int target) {
        // Your solution here
        
    }
}`,
      cpp: `class Solution {
public:
    vector<int> yourSolution(vector<int>& nums, int target) {
        // Your solution here
        
    }
};`,
    };
    return templates[lang as keyof typeof templates] || templates.javascript;
  };

  const [code, setCode] = useState(getInitialCode('python'));
  const isConnected = status.value === 'connected';

  // Code tracking functions
  const trackCodeChange = (newCode: string) => {
    const timestamp = Date.now();
    setCodeHistory((prev) => [...prev, { timestamp, code: newCode }]);
  };

  // Enhanced setCode function with tracking
  const handleCodeChange = (newCode: string) => {
    setCode(newCode);
    trackCodeChange(newCode);
  };

  // Initialize session start time when interview begins
  useEffect(() => {
    if (isConnected && sessionStartTimeRef.current === 0) {
      sessionStartTimeRef.current = Date.now();
      // Track initial code
      trackCodeChange(code);
    }
  }, [isConnected, code, user]);

  // Capture chatGroupId when available
  useEffect(() => {
    if (isConnected && chatMetadata?.chatGroupId && !chatGroupIdRef.current) {
      chatGroupIdRef.current = chatMetadata.chatGroupId;
    }
  }, [isConnected, chatMetadata?.chatGroupId]);

  // Helper function to create session context (reused in analysis and reporting)
  const createSessionContext = useCallback(() => {
    if (!currentProblem) return null;

    return {
      sessionTime: sessionTimer.formattedTime,
      minutesUsed: sessionActions.getMinutesUsed(),
      problemTitle: currentProblem.title,
      difficulty: currentProblem.difficulty,
      problemDescription: currentProblem.description,
      problemConstraints: currentProblem.constraints,
      problemExamples: currentProblem.examples,
      companyContext: currentProblem.company,
      userPreferences: {
        isStructured: isStructuredPreferences,
        isLeetCode: isLeetCodePreferences,
        ...(isLeetCodePreferences && preferences
          ? {
              title: (preferences as LeetCodePrefs).title,
              difficulty: (preferences as LeetCodePrefs).difficulty,
              description: (preferences as LeetCodePrefs).description,
              examples: (preferences as LeetCodePrefs).examples,
              constraints: (preferences as LeetCodePrefs).constraints,
            }
          : isStructuredPreferences && preferences
          ? {
              difficulty: (preferences as StructuredPreferences).difficulty,
              area: (preferences as StructuredPreferences).area,
              companyStyle: (preferences as StructuredPreferences).companyStyle,
            }
          : {
              freeFormText: (preferences as FreeFormPreferences)?.freeFormText,
            }),
      },
    };
  }, [
    currentProblem,
    sessionTimer.formattedTime,
    sessionActions,
    isStructuredPreferences,
    isLeetCodePreferences,
    preferences,
  ]);

  // Intercept all navigation attempts including button clicks and link clicks
  useEffect(() => {
    if (!isConnected) return;

    const handleAllClicks = (e: Event) => {
      const target = e.target as HTMLElement;

      // Check if it's a navigation link
      const link = target.closest('a');
      const linkHref = link?.getAttribute('href') || '';
      // eslint-disable-next-line no-script-url
      const isJavaScriptUrl = linkHref.indexOf('javascript:') === 0;
      if (link && linkHref && !isJavaScriptUrl) {
        const url = new URL(link.href);
        const isInternal = url.origin === window.location.origin;
        const isDifferentPage = url.pathname !== window.location.pathname;

        if (isInternal && isDifferentPage) {
          e.preventDefault();
          e.stopPropagation();
          showNavigationConfirmation(url.pathname);
          return;
        }
      }

      // Check if it's a button that might trigger navigation
      const button = target.closest('button');
      if (button) {
        // Look for common navigation button patterns
        const buttonText = button.textContent?.toLowerCase() || '';
        const isFinishButton =
          buttonText.includes('finish') ||
          buttonText.includes('submit') ||
          buttonText.includes('complete');

        const isNavigationButton =
          !isFinishButton &&
          (buttonText.includes('practice') ||
            buttonText.includes('profile') ||
            buttonText.includes('pricing') ||
            buttonText.includes('login') ||
            buttonText.includes('sign') ||
            buttonText.includes('home') ||
            button.getAttribute('data-navigate') ||
            button.closest('[role="navigation"]') ||
            button.closest('header'));

        if (isNavigationButton) {
          // We can't predict where the button will navigate, so we need to
          // prevent the click and use a more aggressive approach
          e.preventDefault();
          e.stopPropagation();

          // Store the button reference and set up a mutation observer
          // to detect if navigation happens
          showNavigationConfirmation(null, () => {
            // User confirmed - allow the original click
            button.click();
          });
        }
      }
    };

    const showNavigationConfirmation = (
      targetPath?: string | null,
      originalAction?: () => void
    ) => {
      handlePause();

      const shouldLeave = window.confirm(
        'You are currently in an active interview session. Are you sure you want to leave?'
      );

      if (shouldLeave) {
        // User confirmed - record usage and navigate
        if (sessionTimer.elapsedSeconds > 0) {
          recordSessionUsage(false).then(() => {
            if (targetPath) {
              navigate(targetPath);
            } else if (originalAction) {
              originalAction();
            }
          });
        } else {
          if (targetPath) {
            navigate(targetPath);
          } else if (originalAction) {
            originalAction();
          }
        }
      } else {
        // User canceled - resume Hume assistant
        // handleReconnect();
      }
    };

    // Use capture phase to intercept before other handlers
    document.addEventListener('click', handleAllClicks, true);

    return () => {
      document.removeEventListener('click', handleAllClicks, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, navigate]);

  // Handle browser back/forward navigation
  useEffect(() => {
    if (!isConnected) return;

    const handlePopState = (e: PopStateEvent) => {
      // Prevent the navigation
      e.preventDefault();

      // Push the current state back to history to "cancel" the back navigation
      window.history.pushState(null, '', window.location.pathname);

      // Pause and show confirmation
      handlePause();

      const shouldLeave = window.confirm(
        'You are currently in an active interview session. Are you sure you want to leave?'
      );

      if (shouldLeave) {
        // User confirmed - record usage and allow navigation
        if (sessionTimer.elapsedSeconds > 0) {
          recordSessionUsage(false).then(() => {
            // Navigate back to trigger the actual back navigation
            window.history.go(-1);
          });
        } else {
          window.history.go(-1);
        }
      } else {
        // User canceled - resume Hume assistant
        // handleReconnect();
      }
    };

    // Add state to history to detect back navigation
    window.history.pushState(null, '', window.location.pathname);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);

  // Helper function to format array-like strings for AI understanding
  const formatForAI = (str: string | null | undefined): string => {
    // Handle null/undefined cases
    if (!str || typeof str !== 'string') {
      return String(str || '');
    }
    // Convert array notation [1,2,3] to "array [1, 2, 3]"
    return str.replace(/\[([^\]]+)\]/g, (match, content) => {
      // Add spaces after commas if not already present
      const formatted = content.replace(/,(?!\s)/g, ', ');
      return `array of ${formatted}`;
    });
  };

  // Get timer duration based on difficulty
  const getTimerDuration = (difficulty: string): number => {
    switch (difficulty) {
      case 'Easy':
        return 15 * 60; // 15 minutes in seconds
      case 'Medium':
        return 20 * 60; // 20 minutes in seconds
      case 'Hard':
        return 25 * 60; // 25 minutes in seconds
      default:
        return 15 * 60; // Default to 20 minutes
    }
  };

  // Session usage tracking
  const recordSessionUsage = useCallback(
    async (isSubmission: boolean = false) => {
      disconnect();

      if (
        isSubmittingUsage ||
        !sessionTimer.elapsedSeconds ||
        sessionTimer.elapsedSeconds <= INVALID_TIME_IN_MIN * 60
      ) {
        navigate('/practice');
        return;
      }

      setIsSubmittingUsage(true);
      const minutesUsed = sessionActions.getMinutesUsed();

      try {
        await membershipService.updateSessionUsage({
          minutes_used: minutesUsed,
          session_type: 'coding_practice',
        });

        if (isSubmission) {
          // Extract code data and send to finished interview page
          const codeData = analysisService.extractCodeData(
            codeHistory,
            language
          );
          const sessionContext = createSessionContext();

          navigate('/practice/finished', {
            state: {
              chatGroupId: chatGroupIdRef.current,
              codeData,
              sessionContext,
              sessionTime: sessionTimer.formattedTime,
            },
          });
          // navigate('/practice/finished', {
          //   state: {
          //     chatGroupId: 'efefe2b1-d0c4-45b9-a947-57077278b045',
          //     codeData: excellentInterviewRequest.codeData,
          //     sessionContext: excellentInterviewRequest.sessionContext,
          //     sessionTime: sessionTimer.formattedTime,
          //   },
          // });
        } else {
          // For exit without submission, navigate back
          navigate('/practice');
        }
      } catch (error: any) {
        console.error('Failed to record session usage:', error);

        if (error.isUsageError && error.details) {
          // Free user hit limit - still navigate to result page if it's a submission
          sessionActions.stop();

          if (isSubmission) {
            const codeData = analysisService.extractCodeData(
              codeHistory,
              language
            );
            const sessionContext = createSessionContext();

            navigate('/practice/finished', {
              state: {
                chatGroupId: chatGroupIdRef.current,
                codeData,
                sessionContext,
                sessionTime: sessionTimer.formattedTime,
                usageError: error.details,
              },
            });
            // navigate('/practice/finished', {
            //   state: {
            //     chatGroupId: 'efefe2b1-d0c4-45b9-a947-57077278b045',
            //     codeData: excellentInterviewRequest.codeData,
            //     sessionContext: excellentInterviewRequest.sessionContext,
            //     sessionTime: sessionTimer.formattedTime,
            //     usageError: error.details,
            //   },
            // });
          }
        } else {
          // Other errors - still allow navigation
          alert('Failed to record session usage. Please contact support.');
          navigate('/practice');
        }
      } finally {
        setIsSubmittingUsage(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isSubmittingUsage,
      sessionTimer.elapsedSeconds,
      sessionActions,
      currentProblem,
      sessionTimer.formattedTime,
      navigate,
    ]
  );

  const handleSubmitInterview = () => {
    setShowEarlySubmissionModal(true);
  };

  const handleConfirmSubmission = async () => {
    setShowEarlySubmissionModal(false);
    sessionActions.stop();
    await disconnect();
    try {
      await recordSessionUsage(true);
    } catch (error) {
      console.error('Error in handleConfirmSubmission:', error);
    }
  };

  const handleCancelSubmission = () => {
    setShowEarlySubmissionModal(false);
  };

  const handleBackToPractice = () => {
    setShowEarlySubmissionModal(false);
    navigate('/practice');
  };

  const handlePause = useCallback(() => {
    sessionActions.stop();
    setTimerPaused(true);
    disconnect();
  }, [sessionActions, setTimerPaused, disconnect]);

  const handleReconnect = useCallback(async () => {
    const currentChatGroupId = chatGroupIdRef.current;

    sessionActions.start();
    setTimerPaused(false);

    // If we have chatGroupId and token, try to reconnect
    if (currentChatGroupId && accessToken) {
      try {
        await connect({
          auth: { type: 'accessToken', value: accessToken },
          configId: '4c45b5ca-9f84-4e18-ad0d-72b2ff56cff9',
          resumedChatGroupId: currentChatGroupId,
        });
      } catch (error) {
        console.error('Reconnection failed:', error);
        // Fallback to just resuming if reconnection fails
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionActions, accessToken, connect]);

  const handleModalOpen = useCallback(() => {
    // Pause the assistant and timer when modal opens
    handlePause();
  }, [handlePause]);

  const handleModalClose = useCallback(() => {
    // Resume the assistant and timer when modal closes (on cancel)
    // handleReconnect();
  }, []);
  // Usage warning modal handlers
  const handleContinueWithOverage = useCallback(() => {
    setShowUsageWarning(false);
    // handleReconnect();
  }, []);

  const handleExitFromUsageModal = useCallback(async () => {
    setShowUsageWarning(false);
    disconnect();
    sessionActions.stop();
    await recordSessionUsage(true);
  }, [disconnect, sessionActions, recordSessionUsage]);

  // Report issue handlers
  const handleReportIssue = useCallback(() => {
    handlePause();
    setShowReportModal(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmitReport = useCallback(
    async (issueText: string) => {
      if (!currentProblem) {
        throw new Error('No problem available to report');
      }

      // Prepare session context using helper function
      const sessionContext = createSessionContext();
      if (!sessionContext) {
        throw new Error('Failed to create session context');
      }

      const reportData = {
        issueText,
        chatGroupId: chatGroupIdRef.current,
        sessionContext,
      };

      await reportService.submitCodingReport(reportData);
    },
    [currentProblem, createSessionContext]
  );

  // Start timer when interview begins
  const startTimer = useCallback(
    (difficulty: string) => {
      const duration = getTimerDuration(difficulty);

      setTotalTime(duration);
      setTimeRemaining(duration);
      setTimerPaused(false); // Ensure timer is not paused

      // Also start session tracking
      sessionActions.start();
    },
    [sessionActions]
  );

  // Timer countdown effect
  useEffect(() => {
    if (!timerPaused && timeRemaining > 0) {
      timerRef.current = setTimeout(() => {
        setTimeRemaining((prev) => prev - 1);
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [timerPaused, timeRemaining]);

  // Separate effect to handle timer expiration (runs only once when timer expires)
  useEffect(() => {
    if (timeRemaining === 0 && totalTime > 0 && !timerPaused) {
      // Set timer as paused to prevent further triggers
      setTimerPaused(true);

      disconnect();
      sessionActions.stop();
      recordSessionUsage(true);
    }
  }, [
    timeRemaining,
    totalTime,
    timerPaused,
    disconnect,
    sessionActions,
    recordSessionUsage,
  ]);

  // Send timer updates to Hume every 5 seconds when connected
  useEffect(() => {
    if (isConnected && !timerPaused) {
      // Clear existing interval
      if (timerUpdateRef.current) {
        clearInterval(timerUpdateRef.current);
      }

      // Set interval to send timer updates every 5 seconds
      const interval = setInterval(() => {
        // Use ref to get current timeRemaining value
        sendSessionSettings({
          context: {
            text: `
INTERVIEW CONTEXT UPDATE
- Duration: ~${Math.ceil(totalTime / 60)} minutes
- Time remaining: ~${Math.ceil(timeRemainingRef.current / 60)} minutes
- Language: ${language}

CANDIDATE'S CURRENT CODE (authoritative, use this when asked about "current code"):
${code}
`,
            type: 'editable',
          },
        });
      }, 1000);

      timerUpdateRef.current = interval;
    } else {
      // Clear interval when disconnected or paused
      if (timerUpdateRef.current) {
        clearInterval(timerUpdateRef.current);
        timerUpdateRef.current = null;
      }
    }

    return () => {
      if (timerUpdateRef.current) {
        clearInterval(timerUpdateRef.current);
      }
    };
  }, [
    isConnected,
    timerPaused,
    sendSessionSettings,
    code,
    language,
    totalTime,
  ]); // Removed timeRemaining from dependencies

  const connectToHume = useCallback(
    async (problem: Problem, token: string, signal?: AbortSignal) => {
      try {
        const text = getSystemPrompt();

        await connect({
          auth: { type: 'accessToken', value: token },
          configId: '4c45b5ca-9f84-4e18-ad0d-72b2ff56cff9',
          sessionSettings: {
            type: 'session_settings',
            systemPrompt: text,
            variables: {
              candidate_name: user?.first_name || '',
              company: problem.company,
              title: problem.title,
              difficulty: problem.difficulty,
              description: problem.description,
              examples: problem.examples
                .map(
                  (ex, i) =>
                    `Example ${i + 1}:\nInput: ${formatForAI(
                      ex.input
                    )}\nOutput: ${formatForAI(ex.output)}${
                      ex.explanation
                        ? `\nExplanation: ${formatForAI(ex.explanation)}`
                        : ''
                    }`
                )
                .join('\n\n'),
              constraints: problem.constraints.join('\n'),
              language,
              code,
              total_time: Math.ceil(totalTime / 60).toString(),
              remaining_time: Math.ceil(timeRemaining / 60).toString(),
            },
          },
        });
      } catch (error) {
        console.error('Failed to connect to Hume:', error);
        throw error;
      }
    },
    [connect, user, code, language, totalTime, timeRemaining]
  );

  const handlePresetQuestion = useCallback(async () => {
    const question = providedProblem!;
    setIsLoadingQuestion(true); // Then show loading screen
    setQuestionError(null);

    // Create new AbortController for this request sequence
    const ac = new AbortController();
    abortControllerRef.current = ac;

    try {
      // Step 1: Generate question based on preferences
      setLoadingStep('question');
      setCurrentProblem(question);
      setIsLoadingQuestion(false);

      // Check if request was aborted
      if (ac.signal.aborted) return;

      // Step 2: Fetch Hume access token
      setLoadingStep('token');
      const token = await fetchHumeAccessToken(ac.signal);
      setAccessToken(token);

      // Check if request was aborted
      if (ac.signal.aborted) return;

      // Step 3: Auto-connect to Hume AI
      setLoadingStep('connecting');
      await connectToHume(question, token, ac.signal);
      setLoadingStep('ready');

      // Step 4: Start timer based on difficulty
      startTimer(question.difficulty);
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return;
      }
      console.error('Failed to setup interview:', error);
      setQuestionError(
        error instanceof Error ? error.message : 'Failed to setup interview'
      );
      setIsLoadingQuestion(false);
      setLoadingStep('ready'); // Reset loading step to show error screen
    }
  }, [providedProblem, connectToHume, startTimer]);

  const handlePreferencesConfirm = useCallback(
    async (
      prefs: Preferences,
      isStructured: boolean,
      isLeetCode: boolean = false
    ) => {
      setPreferences(prefs);
      setIsStructuredPreferences(isStructured);
      setIsLeetCodePreferences(isLeetCode);
      setShowPreferences(false); // Hide preferences modal first
      setIsLoadingQuestion(true); // Then show loading screen
      setQuestionError(null);
      setLoadingStep('question');

      // Create new AbortController for this request sequence
      const ac = new AbortController();
      abortControllerRef.current = ac;

      try {
        // Step 1: Generate question based on preferences
        setLoadingStep('question');
        let question: Problem;

        if (isLeetCode) {
          // Create problem directly from LeetCode preferences
          question = createLeetCodeProblem(prefs as LeetCodePrefs);
        } else if (isStructured) {
          // Generate question using existing API
          question = await generateQuestion(
            prefs as StructuredPreferences,
            ac.signal
          );
        } else {
          // Generate question from free-form text
          question = await generateFreeFormQuestion(
            (prefs as FreeFormPreferences).freeFormText,
            ac.signal
          );
        }
        setCurrentProblem(question);
        setIsLoadingQuestion(false);

        // Check if request was aborted
        if (ac.signal.aborted) return;

        // Step 2: Fetch Hume access token
        setLoadingStep('token');
        const token = await fetchHumeAccessToken(ac.signal);
        setAccessToken(token);

        // Check if request was aborted
        if (ac.signal.aborted) return;

        // Step 3: Auto-connect to Hume AI
        setLoadingStep('connecting');
        await connectToHume(question, token, ac.signal);
        setLoadingStep('ready');

        // Step 4: Start timer based on difficulty
        startTimer(question.difficulty);
      } catch (error: any) {
        if (error.name === 'AbortError') {
          return;
        }
        console.error('Failed to setup interview:', error);
        setQuestionError(
          error instanceof Error ? error.message : 'Failed to setup interview'
        );
        setIsLoadingQuestion(false);
        setLoadingStep('ready'); // Reset loading step to show error screen
      }
    },
    [connectToHume, startTimer]
  );

  const handleRegenerateQuestion = useCallback(async () => {
    setQuestionError(null);
    setIsLoadingQuestion(true);

    // If we have preferences, retry with those
    if (preferences) {
      await handlePreferencesConfirm(
        preferences,
        isStructuredPreferences,
        isLeetCodePreferences
      );
    } else if (providedProblem) {
      // If we have a provided problem, retry preset question
      await handlePresetQuestion();
    } else {
      // Otherwise, show preferences modal again
      setShowPreferences(true);
    }
  }, [
    preferences,
    isStructuredPreferences,
    isLeetCodePreferences,
    providedProblem,
    handlePreferencesConfirm,
    handlePresetQuestion,
  ]);

  if (isLoadingQuestion || !['ready'].includes(loadingStep)) {
    return (
      <div className='h-full flex flex-col items-center justify-center bg-gray-50 relative'>
        {/* Technical Loading Animation */}
        <div className='relative w-20 h-20 mx-auto mb-8'>
          {/* Outer rotating ring */}
          <div className='absolute inset-0 border-4 border-gray-200 rounded-full'></div>
          <div className='absolute inset-0 border-4 border-transparent border-t-blue-600 border-r-blue-600 rounded-full animate-spin'></div>

          {/* Inner pulsing circle */}
          <div className='absolute inset-3 bg-blue-100 rounded-full animate-pulse'></div>

          {/* Center icon */}
          <div className='absolute inset-0 flex items-center justify-center'>
            <div className='w-6 h-6 text-blue-600'>
              <svg
                viewBox='0 0 24 24'
                fill='currentColor'
              >
                <path d='M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0L19.2 12l-4.6-4.6L16 6l6 6-6 6-1.4-1.4z' />
              </svg>
            </div>
          </div>
        </div>

        {/* Technical Status Messages */}
        <div className='text-xl font-mono text-gray-800 mb-6'>
          {loadingStep === 'question' && 'Generating Question...'}
          {loadingStep === 'token' && 'Finding AI interviewer...'}
          {loadingStep === 'connecting' && 'Connecting to AI interviewer...'}
        </div>

        {/* Audio Volume Warning */}
        <div className='w-80 mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg'>
          <div className='flex items-start space-x-3'>
            <div className='flex-shrink-0 mt-1'>
              <svg
                className='h-5 w-5 text-yellow-600'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z'
                />
              </svg>
            </div>
            <div>
              <p className='text-base font-medium text-yellow-800 mb-1'>
                Audio Volume Check
              </p>
              <p className='text-sm text-yellow-700'>
                Please ensure your audio volume is turned up for the best
                interview experience. You'll be hearing and speaking with an AI
                interviewer.
              </p>
            </div>
          </div>
        </div>

        {/* Progress Steps - Fixed Position */}
        <div className='w-80 space-y-3 text-sm font-mono'>
          <div className='flex items-center space-x-3'>
            <div className='w-3 h-3 bg-green-500 rounded-full flex-shrink-0'></div>
            <span className='text-green-600'>✓ Preferences received</span>
          </div>

          <div className='flex items-center space-x-3'>
            <div
              className={`w-3 h-3 rounded-full flex-shrink-0 ${
                loadingStep === 'question'
                  ? 'bg-blue-500 animate-pulse'
                  : ['token', 'connecting', 'ready'].includes(loadingStep)
                  ? 'bg-green-500'
                  : 'bg-gray-300'
              }`}
            ></div>
            <span
              className={
                loadingStep === 'question'
                  ? 'text-blue-600'
                  : ['token', 'connecting', 'ready'].includes(loadingStep)
                  ? 'text-green-600'
                  : 'text-gray-400'
              }
            >
              {loadingStep === 'question'
                ? '→'
                : ['token', 'connecting', 'ready'].includes(loadingStep)
                ? '✓'
                : '○'}{' '}
              Generating question...
            </span>
          </div>

          <div className='flex items-center space-x-3'>
            <div
              className={`w-3 h-3 rounded-full flex-shrink-0 ${
                loadingStep === 'token'
                  ? 'bg-blue-500 animate-pulse'
                  : ['connecting', 'ready'].includes(loadingStep)
                  ? 'bg-green-500'
                  : 'bg-gray-300'
              }`}
            ></div>
            <span
              className={
                loadingStep === 'token'
                  ? 'text-blue-600'
                  : ['connecting', 'ready'].includes(loadingStep)
                  ? 'text-green-600'
                  : 'text-gray-400'
              }
            >
              {loadingStep === 'token'
                ? '→'
                : ['connecting', 'ready'].includes(loadingStep)
                ? '✓'
                : '○'}{' '}
              Finding AI interviewer...
            </span>
          </div>

          <div className='flex items-center space-x-3'>
            <div
              className={`w-3 h-3 rounded-full flex-shrink-0 ${
                loadingStep === 'connecting'
                  ? 'bg-blue-500 animate-pulse'
                  : loadingStep === 'ready'
                  ? 'bg-green-500'
                  : 'bg-gray-300'
              }`}
            ></div>
            <span
              className={
                loadingStep === 'connecting'
                  ? 'text-blue-600'
                  : loadingStep === 'ready'
                  ? 'text-green-600'
                  : 'text-gray-400'
              }
            >
              {loadingStep === 'connecting'
                ? '→'
                : loadingStep === 'ready'
                ? '✓'
                : '○'}{' '}
              Connecting to AI interviewer...
            </span>
          </div>
        </div>

        {showPreferences && (
          <PreferencesModal
            onConfirm={handlePreferencesConfirm}
            onCancel={() => navigate('/')}
          />
        )}
      </div>
    );
  }

  if (questionError) {
    return (
      <div className='h-full flex items-center justify-center bg-gray-50'>
        <div className='text-center max-w-md'>
          <div className='mb-6'>
            <div className='w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4'>
              <svg
                className='w-8 h-8 text-red-600'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z'
                />
              </svg>
            </div>
            <h2 className='text-xl font-semibold text-gray-900 mb-2'>
              Question Generation Failed
            </h2>
            <p className='text-sm text-gray-600 mb-4'>{questionError}</p>
          </div>

          <div className='space-y-3'>
            <button
              onClick={handleRegenerateQuestion}
              className='w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors'
            >
              Regenerate Question
            </button>
            <button
              onClick={() => navigate('/practice')}
              className='w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors'
            >
              Back to Practice
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!currentProblem) {
    return (
      <div className='h-full flex items-center justify-center'>
        <div className='text-lg text-gray-600'>No question available</div>
      </div>
    );
  }

  return (
    <div className='h-full p-6 grid grid-cols-2 gap-6 relative'>
      <div className='flex flex-col'>
        <ProblemPanel
          problem={currentProblem}
          onReportIssue={handleReportIssue}
        />
      </div>

      <div className='flex flex-col gap-4'>
        <div className='flex-1'>
          <CodeEditor
            language={language}
            code={code}
            onLanguageChange={setLanguage}
            onCodeChange={handleCodeChange}
            getInitialCode={getInitialCode}
          />
        </div>

        <div className='h-20'>
          <AudioControls
            timeRemaining={timeRemaining}
            timerPaused={timerPaused}
            onSessionPause={handlePause}
            onSessionResume={handleReconnect}
            onFinishInterview={handleSubmitInterview}
          />
        </div>
      </div>

      {/* Early Submission Modal */}
      <EarlySubmissionModal
        isOpen={showEarlySubmissionModal}
        elapsedMinutes={Math.floor(sessionTimer.elapsedSeconds / 60)}
        onConfirm={handleConfirmSubmission}
        onCancel={handleCancelSubmission}
        onBackToPractice={handleBackToPractice}
        onModalOpen={handleModalOpen}
        onModalClose={handleModalClose}
      />

      {/* Usage Warning Modal */}
      <UsageWarningModal
        isOpen={showUsageWarning}
        onClose={() => setShowUsageWarning(false)}
        onContinue={handleContinueWithOverage}
        onExit={handleExitFromUsageModal}
        type={usageWarningType}
        hasActiveMembership={
          membershipInfo
            ? membershipInfo.billing_period.subscription_status === 'active' &&
              membershipInfo.tier > 1
            : false
        }
        remainingMinutes={
          membershipInfo
            ? Math.max(
                0,
                membershipInfo.remaining_minutes -
                  sessionActions.getMinutesUsed()
              )
            : 0
        }
      />

      {/* Report Modal */}
      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        onSubmit={handleSubmitReport}
        problemTitle={currentProblem?.title || 'Unknown Problem'}
      />
    </div>
  );
};

const InterviewPage: React.FC = () => {
  return (
    <VoiceProvider>
      <InterviewContent />
    </VoiceProvider>
  );
};

export default InterviewPage;
